
import React, { useState, useRef, useEffect } from "react";
import {
  Box, Grid, Card, Typography, TextField, Button,
  Checkbox, FormControlLabel, IconButton, InputAdornment,
  CircularProgress, useMediaQuery, useTheme, ToggleButtonGroup,
  ToggleButton, Alert, Collapse
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import ayujalogo from "../../Logos/AuthScreens/AuthLogo.png";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Footer from "../../Common/Footer";
import { fetchUserFromRedis } from "./Register";
import CustomDialog from "../../../CustomDialog";
import AppDownloadSection from "../AppDownloadSection";
import QRCode from "react-qr-code";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.ayuja.app&hl=en_IN";
const APP_STORE_URL = "https://apps.apple.com/in/app/ayuja/id6758427368";
const API_BASE = process.env.REACT_APP_API_BASE_URL;

// ─── OTP digit input ──────────────────────────────────────────────────────────
const OtpInput = ({ value, onChange, disabled }) => {
  const digits = value.split("").concat(Array(6).fill("")).slice(0, 6);
  const refs = useRef([]);

  const handleKey = (e, idx) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      refs.current[idx - 1].focus();
    }
  };

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[idx] = val;
    onChange(next.join(""));
    if (val && idx < 5) refs.current[idx + 1].focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(pasted);
    refs.current[Math.min(pasted.length, 5)]?.focus();
    e.preventDefault();
  };

  return (
    <Box sx={{ display: "flex", gap: 1, justifyContent: "center", my: 2 }}>
      {digits.map((d, i) => (
        <TextField
          key={i}
          inputRef={(el) => (refs.current[i] = el)}
          value={d}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKey(e, i)}
          onPaste={i === 0 ? handlePaste : undefined}
          disabled={disabled}
          inputProps={{
            maxLength: 1,
            style: { textAlign: "center", fontSize: "22px", fontWeight: "bold", padding: "10px 0" },
          }}
          sx={{
            width: 44,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              "&.Mui-focused fieldset": { borderColor: "#007a85", borderWidth: 2 },
            },
          }}
        />
      ))}
    </Box>
  );
};
function PlayStoreBadge({ url }) {
  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        backgroundColor: "#000",
        borderRadius: "10px",
        px: 2,
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        textTransform: "none",
        color: "#fff",
        minWidth: 160,
        "&:hover": { backgroundColor: "#222" },
      }}
    >
      {/* Google Play icon */}
      <svg width="28" height="28" viewBox="0 0 512 512" fill="none">
        <path d="M70 7.7L291 256 70 504.3A28 28 0 0156 480V32a28 28 0 0114-24.3z" fill="#EA4335"/>
        <path d="M386 196l-57-33-78 93 78 93 58-33c22-13 22-107-1-120z" fill="#FBBC04"/>
        <path d="M70 7.7A28 28 0 0196 4l239 139-78 93L70 7.7z" fill="#4285F4"/>
        <path d="M70 504.3l187-228 78 93L96 508a28 28 0 01-26-3.7z" fill="#34A853"/>
      </svg>
      <Box textAlign="left">
        <Typography sx={{ fontSize: "10px", color: "#ccc", lineHeight: 1 }}>GET IT ON</Typography>
        <Typography sx={{ fontSize: "16px", fontWeight: "bold", lineHeight: 1.3 }}>Google Play</Typography>
      </Box>
    </Button>
  );
}

function AppStoreBadge({ url }) {
  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        backgroundColor: "#000",
        borderRadius: "10px",
        px: 2,
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        textTransform: "none",
        color: "#fff",
        minWidth: 160,
        "&:hover": { backgroundColor: "#222" },
      }}
    >
      {/* Apple icon */}
      <svg width="26" height="26" viewBox="0 0 814 1000" fill="white">
        <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-42.4-155.5-127.4C46.7 790.7 0 663 0 541.8c0-207.5 134.4-317.5 266.5-317.5 99.8 0 183.2 65.8 245.8 65.8 60.4 0 156.2-69.2 270.1-69.2zM549.8 149.1c-5.6-36.5-1.4-72.9 11.4-104.7 34.3-12.7 75.3.1 101.2 27.6 26.2 27.8 40.5 64.5 39.8 101.4-36.5 5.9-75.6-6-107.8-24.3z"/>
      </svg>
      <Box textAlign="left">
        <Typography sx={{ fontSize: "10px", color: "#ccc", lineHeight: 1 }}>Available on the</Typography>
        <Typography sx={{ fontSize: "16px", fontWeight: "bold", lineHeight: 1.3 }}>App Store</Typography>
      </Box>
    </Button>
  );
}

// ─── Countdown timer ──────────────────────────────────────────────────────────
const useCountdown = (seconds) => {
  const [remaining, setRemaining] = useState(0);
  const start = () => setRemaining(seconds);
  useEffect(() => {
    if (remaining <= 0) return;
    const t = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(t);
  }, [remaining]);
  return [remaining, start];
};

// ═════════════════════════════════════════════════════════════════════════════
const Login = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [user, setUser] = useState(null);
  const [loginMode, setLoginMode] = useState("password");

  // Password state
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ identifier: "", password: "" });

  // OTP state
  const [otpIdentifier, setOtpIdentifier] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpChannel, setOtpChannel] = useState("");
  const [otp, setOtp] = useState("");
  const [countdown, startCountdown] = useCountdown(60);
  const [communities, setCommunities] = useState([]);

  // Shared
  const [loading, setLoading] = useState(false);
  const [inlineError, setInlineError] = useState("");
  const loginAPI = process.env.REACT_APP_LOGIN_API;
  const [dialog, setDialog] = useState({ open: false, title: "", message: "", type: "info" });

  const showDialog = (title, message, type) =>
    setDialog({ open: true, title, message, type });

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  // ── Mode switch ───────────────────────────────────────────────────────────
  const switchMode = (_, val) => {
    if (!val) return;
    setLoginMode(val);
    setInlineError("");
    setOtpSent(false);
    setOtp("");
  };
useEffect(() => {
    fetchCommunities()
   },[])
  const fetchCommunities = async () => {
  
      try {
  
        const response = await axios.get(
          `${API_BASE}/community/config`
        );
  
        setCommunities(
          response.data.communities || []
        );
        sessionStorage.setItem("communities",JSON.stringify(response.data.communities))
  
      } catch (error) {
  
        console.error(
          "Error fetching communities:",
          error
        );
      } 
    };
  // ── Shared post-login handler ─────────────────────────────────────────────
  const finishLogin = (res) => {
    const { user_id, role, approval_status, is_approved } = res.data.user;
    const token = res.data.token;

    if (approval_status === "pending" || !is_approved) {
      showDialog("Account Pending Approval", "Your account is awaiting admin approval.", "warning");
      return;
    }
    if (approval_status === "rejected") {
      showDialog("Account Not Approved", "Your registration was rejected. Contact support.", "error");
      return;
    }
    if (role === "admin" || role === "superadmin") {
      showDialog("Access Denied", "Admins cannot use this resident app.", "error");
      return;
    }

    sessionStorage.setItem("token", token);
    sessionStorage.setItem("userId", user_id);
    fetchUserFromRedis(user_id, token).catch(() => {});
    showDialog("Login Successful", "Welcome back!", "success");
    setTimeout(() => navigate(role === "resident" ? "/services" : "/",{replace: true}), 1500);
  };


  // ── Password login ────────────────────────────────────────────────────────
  const handleLogin = async () => {
    setLoading(true);
    try {
      const res = await axios.post(loginAPI, formData);
      const userId = res.data.user.user_id;
      const token = res.data.token;
      const userRole = res.data.user.role;
      const approvalStatus = res.data.user.approval_status;
      const isApproved = res.data.user.is_approved;

      if (approvalStatus === "pending" || !isApproved) {
        setDialog({ open: true, title: "Account Pending Approval", message: "Your account is awaiting admin approval. You will be notified once approved.", type: "warning" });
        setLoading(false);
        return;
      }
      if (approvalStatus === "rejected") {
        setDialog({ open: true, title: "Account Not Approved", message: "Your registration was Rejected. Please contact support for more information.", type: "error" });
        setLoading(false);
        return;
      }
      if (approvalStatus !== "approved") {
        setDialog({ open: true, title: "Account Access Denied", message: "Your account status is invalid. Please contact support.", type: "error" });
        setLoading(false);
        return;
      }

      if (userRole !== "resident") {
        setDialog({
          open: true,
          title: "Access Denied",
          message: "This app is only for residents. Please use the appropriate portal to sign in.",
          type: "error"
        });
        setLoading(false);
        return;
      }

      sessionStorage.setItem("token", token);
      sessionStorage.setItem("userId", userId);

      if (userRole === "admin" || userRole === "superadmin") {
        setDialog({ open: true, title: "Access Denied", message: "Admins cannot use this resident app.", type: "error" });
        setLoading(false);
        return;
      }

      try {
        const userData = await fetchUserFromRedis(userId, token);
        setUser(userData);
      } catch (redisErr) {
        console.warn("Could not fetch user from Redis:", redisErr);
      }

      setDialog({ open: true, title: "Login Successful", message: "Welcome back!", type: "success" });

      setTimeout(() => {
        if (userRole === "resident") navigate("/services",{replace: true});
        else navigate("/");
      }, 1500);

    } catch (err) {
      console.error("Login error:", err);
             if (!err.response) {
    // No response = network/DNS/unreachable error, not a server error
    setDialog({
      open: true,
      title: "Connection Error",
      message: "Could not reach the server. Please check your connection and try again.",
      type: "error",
    });
    setLoading(false);
    return;
  }
      if (err.response?.status === 403) {
        const errorData = err.response.data;
        const status = errorData.status;
        if (status === "pending_approval") {
          setDialog({ open: true, title: "Account Pending Approval", message: errorData.error || "Your account is awaiting admin approval.", type: "warning" });
        } else if (status === "rejected") {
          setDialog({ open: true, title: "Account Not Approved", message: errorData.error || "Your registration was not approved.", type: "error" });
        } else {
          setDialog({ open: true, title: "Access Denied", message: errorData.error || "You do not have permission to access this application.", type: "error" });
        }
      } else if (err.response?.status === 401) {
        setDialog({ open: true, title: "Login Failed", message: "Invalid email/phone or password. Please try again.", type: "error" });
      } else if (err.response?.status === 404) {
        setDialog({ open: true, title: "User Not Found", message: "No account found with this email or phone number.", type: "error" });
      } else {
        setDialog({ open: true, title: "Login Failed", message: err.response?.data?.message || "An error occurred. Please try again.", type: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  // ── OTP: send ─────────────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    setInlineError("");
    if (!otpIdentifier.trim()) {
      setInlineError("Please enter your email or phone number.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/send-login-otp`, {
        identifier: otpIdentifier.trim(),
      });

      if (res.data.success) {
        setOtpSent(true);
        setOtpChannel(res.data.channel);
        startCountdown();
      } else {
        const status = res.data.status;
        if (status === "pending_approval") {
          showDialog(
            "Account Pending Approval",
            "Your account is awaiting admin approval. Once approved, you can log in using OTP or password.",
            "warning"
          );
        } else if (status === "rejected") {
          showDialog(
            "Account Not Approved",
            "Your registration was not approved. Please contact support.",
            "error"
          );
        } else if (status === "non_resident") {
          showDialog(
            "Use Password Login",
            "login is only available for residents.",
            "info"
          );
          switchMode(null, "password");
        } else if (status === "access_denied") {
          showDialog(
            "Account Access Restricted",
            "Your account access is restricted. Please contact support.",
            "error"
          );
        } else {
          setInlineError(res.data.message || "Failed to send OTP.");
        }
      }
} catch (err) {
  const data = err.response?.data || {};
  const status = data.status;
  const message = data.message;

  if (status === "pending_approval") {
    showDialog(
      "Account Pending Approval",
      message || "Your account is awaiting admin approval. Once approved, you can log in.",
      "warning"
    );
  } else if (status === "rejected") {
    showDialog(
      "Account Not Approved",
      message || "Your registration was not approved. Please contact support.",
      "error"
    );
  } else if (status === "non_resident") {
    showDialog(
      "Use Password Login",
      message || "Login is only available for residents.",
      "info"
    );
    switchMode(null, "password");
  } else if (status === "access_denied") {
    showDialog(
      "Account Access Restricted",
      message || "Your account access is restricted. Please contact support.",
      "error"
    );
  } else {
    setInlineError(message || "Failed to send OTP. Try again.");
  }
} finally {
      setLoading(false);
    }
  };

  // ── OTP: resend ───────────────────────────────────────────────────────────
  const handleResendOtp = async () => {
    setOtp("");
    setInlineError("");
    await handleSendOtp();
  };

  // ── OTP: verify ───────────────────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    setInlineError("");
    if (otp.length < 6) {
      setInlineError("Please enter the complete 6-digit OTP.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/verify-login-otp`, {
        identifier: otpIdentifier.trim(),
        otp,
      });
      finishLogin({ data: res.data });
    } catch (err) {
      const msg = err.response?.data?.message || "Invalid OTP. Please try again.";
      setInlineError(msg);
      if (err.response?.status === 429) {
        setOtpSent(false);
        setOtp("");
      }
    } finally {
      setLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════════
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "75vh",
          p: { xs: 2, sm: 3, md: 4 },
          backgroundColor: "#fff",
        }}
      >
        <Card
          sx={{
            borderRadius: "16px",
            maxWidth: { xs: "100%", sm: "520px", md: "1160px" },
            mx: "auto",
            width: "100%",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Grid container sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}>

            {/* ── LEFT PANEL ────────────────────────────────────────────── */}
            <Grid
              item xs={12} md={6}
              sx={{
                background: "linear-gradient(135deg, #004f54, #007a85)",
                color: "white",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                p: { xs: 3, sm: 3, md: 4 },
                flex: { md: "0 0 50%" },
                maxWidth: { md: "80%" },
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 1, md: 2 } }}>
                <img
                  src={ayujalogo}
                  alt="Ayuja Logo"
                  style={{ height: isMobile ? "56px" : isTablet ? "64px" : "80px", width: "auto" }}
                />
              </Box>

              <Typography
                variant={isMobile ? "h6" : "h4"}
                sx={{ fontWeight: "bold", mb: { xs: 0.5, md: 2 }, textAlign: "center" }}
              >
                {isMobile ? "Welcome Back to Ayuja" : "Welcome\nBack to Ayuja"}
              </Typography>

              {!isMobile && (
                <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                  Your trusted partner in personalized in-home care for every stage of life
                </Typography>
              )}

              <Box sx={{ display: "flex", justifyContent: "center", gap: { xs: 2, md: 3 }, mb: { xs: 1.5, md: 4 }, flexWrap: "wrap" }}>
                <Typography variant="body2">✅ 24/7 Support</Typography>
                <Typography variant="body2">✅ Certified Caregivers</Typography>
              </Box>
               {!isMobile && (<>
                    <Grid item xs={12} md={5}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                              alignItems: "center",
                              justifyContent: "center",
                              gap: { xs: 3, sm: 4 },
                            }}
                          >
                            {/* Play Store QR */}
                            <Box sx={{ textAlign: "center" }}>
                              <Box
                                sx={{
                                  background: "#fff",
                                  borderRadius: 3,
                                  p: 2,
                                  boxShadow: "0 4px 20px rgba(8,124,124,0.15)",
                                  border: "2px solid #22577A",
                                  display: "inline-block",
                                }}
                              >
                                <QRCode
                                  value={PLAY_STORE_URL}
                                  size={130}
                                  fgColor="#22577A"
                                  bgColor="#fff"
                                />
                              </Box>
                              <Typography
                                sx={{
                                  mt: 1.5,
                                  fontSize: "13px",
                                  fontFamily: '"Montserrat", sans-serif',
                                  fontWeight: 600,
                                  color: "#ffffff",
                                }}
                              >
                                Scan for Android
                              </Typography>
                            </Box>
              
                            {/* App Store QR */}
                            <Box sx={{ textAlign: "center" }}>
                              <Box
                                sx={{
                                  background: "#fff",
                                  borderRadius: 3,
                                  p: 2,
                                  boxShadow: "0 4px 20px rgba(8,124,124,0.15)",
                                  border: "2px solid #087c7c",
                                  display: "inline-block",
                                }}
                              >
                                <QRCode
                                  value={APP_STORE_URL}
                                  size={130}
                                  fgColor="#087c7c"
                                  bgColor="#fff"
                                />
                              </Box>
                              <Typography
                                sx={{
                                  mt: 1.5,
                                  fontSize: "13px",
                                  fontFamily: '"Montserrat", sans-serif',
                                  fontWeight: 600,
                                  color: "#ffffff",
                                }}
                              >
                                Scan for iOS
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                <Typography
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#ffffff",
                  mb: 2,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  justifyContent: { xs: "center", md: "flex-start" },
                  alignItems: "center",
                }}
              >
                <PlayStoreBadge url={PLAY_STORE_URL} />
                <AppStoreBadge url={APP_STORE_URL} />
              </Box></>)}
               {isMobile && (
                <Button
                  variant="outlined" size="small"
                  onClick={() => navigate("/contactus")}
                  sx={{ borderColor: "#80ED99", color: "#80ED99", textTransform: "none", borderRadius: "8px", fontSize: "12px" }}
                >
                  🚨 Contact Emergency
                </Button>
              )}
            </Grid>

            {/* ── RIGHT PANEL ───────────────────────────────────────────── */}
            <Grid
              item xs={12} md={6}
              sx={{
                backgroundColor: "white",
                p: { xs: 3, sm: 4, md: 4 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                flex: { md: "0 0 50%" },
                maxWidth: { md: "50%" },
              }}
            >
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" mb={0.5}>
                Sign In
              </Typography>
              <Typography variant="body2" mb={2} color="text.secondary">
                Access your personalized care dashboard
              </Typography>

              {/* ── Mode toggle ── */}
              <ToggleButtonGroup
                value={loginMode}
                exclusive
                onChange={switchMode}
                fullWidth
                size="small"
                sx={{ mb: 2.5 }}
              >
                <ToggleButton
                  value="password"
                  sx={{
                    textTransform: "none", fontWeight: 600, gap: 0.5,
                    "&.Mui-selected": { backgroundColor: "#007a85", color: "#fff", "&:hover": { backgroundColor: "#005f64" } },
                  }}
                >
                  <LockOutlinedIcon fontSize="small" /> Password
                </ToggleButton>
                <ToggleButton
                  value="otp"
                  sx={{
                    textTransform: "none", fontWeight: 600, gap: 0.5,
                    "&.Mui-selected": { backgroundColor: "#007a85", color: "#fff", "&:hover": { backgroundColor: "#005f64" } },
                  }}
                >
                  <PhoneAndroidOutlinedIcon fontSize="small" /> OTP
                </ToggleButton>
              </ToggleButtonGroup>

              {/* ── Inline error ── */}
              <Collapse in={!!inlineError}>
                <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setInlineError("")}>
                  {inlineError}
                </Alert>
              </Collapse>

              {/* ══════════ PASSWORD MODE ══════════ */}
              {loginMode === "password" && (
                <>
                  <TextField
                    label="Email or Phone"
                    name="identifier"
                    value={formData.identifier}
                    onChange={handleChange}
                    placeholder="Enter email or 10-digit phone"
                    fullWidth margin="normal"
                    size={isMobile ? "small" : "medium"}
                  />
                  <TextField
                    label="Password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    type={showPassword ? "text" : "password"}
                    fullWidth margin="normal"
                    size={isMobile ? "small" : "medium"}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 0.5 }}>
                    <FormControlLabel
                      control={<Checkbox size="small" />}
                      label={<Typography variant="body2">Remember me</Typography>}
                    />
                    <Button variant="text" size="small" sx={{ textTransform: "none", fontSize: "13px" }} onClick={() => navigate("/ForgotPassword")}>
                      Forgot Password?
                    </Button>
                  </Box>
                  <Button
                    variant="contained" fullWidth
                    sx={{ backgroundColor: "#007a85", "&:hover": { backgroundColor: "#005f64" }, mb: 2, py: isMobile ? 1 : 1.4, fontSize: isMobile ? "14px" : "15px" }}
                    onClick={handleLogin}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={22} color="inherit" /> : "Sign In"}
                  </Button>
                </>
              )}

              {/* ══════════ OTP MODE ══════════ */}
              {loginMode === "otp" && (
                <>
                  <TextField
                    label="Email or Phone"
                    value={otpIdentifier}
                    onChange={(e) => { setOtpIdentifier(e.target.value); setInlineError(""); }}
                    placeholder="Enter email or 10-digit phone"
                    fullWidth margin="normal"
                    size={isMobile ? "small" : "medium"}
                    disabled={otpSent}
                    onKeyDown={(e) => !otpSent && e.key === "Enter" && handleSendOtp()}
                  />

                  {!otpSent ? (
                    <Button
                      variant="contained" fullWidth
                      sx={{ backgroundColor: "#007a85", "&:hover": { backgroundColor: "#005f64" }, mt: 1, mb: 2, py: isMobile ? 1 : 1.4 }}
                      onClick={handleSendOtp}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={22} color="inherit" /> : "Send OTP"}
                    </Button>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
                        OTP sent to your <strong>{otpChannel === "email" ? "email" : "mobile"}</strong>.
                        Enter the 6-digit code below.
                      </Typography>

                      <OtpInput value={otp} onChange={setOtp} disabled={loading} />

                      <Button
                        variant="contained" fullWidth
                        sx={{ backgroundColor: "#007a85", "&:hover": { backgroundColor: "#005f64" }, mb: 1.5, py: isMobile ? 1 : 1.4 }}
                        onClick={handleVerifyOtp}
                        disabled={loading || otp.length < 6}
                      >
                        {loading ? <CircularProgress size={22} color="inherit" /> : "Verify & Sign In"}
                      </Button>

                      {/* Resend countdown */}
                      <Box sx={{ textAlign: "center" }}>
                        {countdown > 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Resend OTP in <strong>{countdown}s</strong>
                          </Typography>
                        ) : (
                          <Button
                            variant="text" size="small"
                            sx={{ textTransform: "none", color: "#007a85", fontWeight: 600 }}
                            onClick={handleResendOtp}
                            disabled={loading}
                          >
                            Resend OTP
                          </Button>
                        )}
                      </Box>

                      {/* Change identifier */}
                      <Box sx={{ textAlign: "center", mt: 0.5 }}>
                        <Button
                          variant="text" size="small"
                          sx={{ textTransform: "none", fontSize: "12px", color: "text.secondary" }}
                          onClick={() => { setOtpSent(false); setOtp(""); setInlineError(""); }}
                        >
                          ← Change email / phone
                        </Button>
                      </Box>
                    </>
                  )}
                </>
              )}

              {/* ── Sign up link ── */}
              <Typography
                textAlign="center" variant="body2"
                sx={{ mt: loginMode === "otp" && !otpSent ? 0 : undefined }}
              >
                Don't have an account?{" "}
                <a href="/Register" style={{ color: "#007a85", fontWeight: "bold" }}>
                  Sign Up
                </a>
              </Typography>
            </Grid>
          </Grid>
        </Card>

        <CustomDialog
          open={dialog.open}
          title={dialog.title}
          message={dialog.message}
          type={dialog.type}
          onClose={() => setDialog({ ...dialog, open: false })}
        />
      </Box>
      {isMobile && (<AppDownloadSection/>)}
      <Footer />
    </>
  );
};

export default Login;